// 무거운 화면 쿼리 캐시의 디스크 영속화 — "새로고침·재접속 즉시 렌더".
//
// 저장 범위: 정책(시세표·리베이트 합산)에 더해 CRM 고객목록·재고(inventory)까지 디스크에 남긴다
// (PERSIST_DOMAINS = policy·crm·inventory, 아래 참조). 셋 다 수백 행~1MB+ 응답이라, react-query
// 가 메모리 전용이면 새로고침/재접속/다음날 방문마다 "빈 화면 → 대용량 다운로드 → 파싱" 풀 사이클을
// 다시 돈다(느린 PC 체감의 핵심). 부팅 시 IndexedDB 에서 직전 캐시를 hydrate 해 "어제 본 표"를
// 0ms 로 띄우고, 각 쿼리의 staleTime·refetchOnWindowFocus 규칙대로 뒤에서 조용히 재검증한다
// (stale-while-revalidate). 참고: 정책 쿼리는 staleTime 0(매번 즉시 stale)이라 hydrate 직후
// 곧바로 백그라운드 재검증이 돈다.
//
// 외부 패키지 없이 react-query 코어의 dehydrate/hydrate + raw IndexedDB 만 쓴다
// (@tanstack/react-query-persist-client 가 하는 일을 이 파일이 직접 한다 — 의존성 0).
//
// 안전(돈이 걸린 가격 + 개인정보):
//  · policy·crm·inventory 쿼리 + 성공 상태만 디스크로. 나머지(인증·설정 등)는 안 남긴다.
//  · CRM 은 고객 개인정보를 담으므로 보존을 다음 세 겹으로 가둔다:
//     - 매장(tenantId)별로 키를 분리(persistKey) — 같은 PC 를 다른 매장 계정으로 로그인해도
//       남의 매장 고객/가격이 섞여 보이지 않는다.
//     - 24h maxAge(+ BUSTER) 로 오래된/구조 바뀐 캐시는 부팅 시 자동 폐기 — 고객 데이터가
//       하루 넘게 디스크에 눌러앉지 않는다.
//     - 로그아웃 시 전체 비움(clearPersistedPolicyCache → idbClearAll). 모든 매장 캐시를 지운다.
//  · 서버 버전스탬프 무효화가 SSOT 이므로 디스크 캐시가 잠깐 stale 해도 재검증으로 교정된다.
import { dehydrate, hydrate, type DehydratedState, type QueryClient } from "@tanstack/react-query";

const DB_NAME = "pb-rq-cache";
const STORE = "kv";
/** 쿼리 키 구조·셀 스키마가 바뀌면 올린다 → 옛 디스크 캐시 자동 폐기(구조 불일치 화면 깨짐 방지). */
const BUSTER = "v2";
/** 이 시간보다 오래된 디스크 캐시는 부팅 시 버린다(하루 지난 가격을 들고 있지 않게). */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** 잦은 캐시 변경을 모아 한 번만 디스크에 쓰기 위한 디바운스. */
const FLUSH_DEBOUNCE_MS = 5000;
/** 한 매장이 브라우저 디스크에 쌓을 최대 직렬화 크기. 큰 표 여러 날짜가 무한히 누적되지 않게 한다. */
const MAX_PERSIST_BYTES = 16 * 1024 * 1024;

type PersistedBlob = { buster: string; savedAt: number; state: unknown };

// ── raw IndexedDB KV (외부 의존 없음) ─────────────────────────────
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbReq<T>(run: (store: IDBObjectStore) => IDBRequest, mode: IDBTransactionMode): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = run(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
      }),
  );
}

const idbGet = <T>(key: string) => idbReq<T>((s) => s.get(key), "readonly");
const idbSet = (key: string, val: unknown) => idbReq<void>((s) => s.put(val, key), "readwrite");
const idbClearAll = () => idbReq<void>((s) => s.clear(), "readwrite");

// ── persist 정책 ──────────────────────────────────────────────────
function persistKey(tenantId: string | null): string {
  return `policy-cache:${BUSTER}:${tenantId ?? "anon"}`;
}

// 디스크로 보존할 쿼리 도메인 — 무거운 목록/표라 첫 진입 0ms 복원의 이득이 큰 화면들.
// 정책(시세표·파이프라인·캘린더)에 더해 CRM 고객목록·재고도 포함한다(둘 다 수백 행을 받아
// 클라에서 거르는 화면이라 콜드 진입이 길었다). 인증·설정 등 가벼운 쿼리는 보존하지 않는다.
// 보존해도 정확성은 안전 — hydrate 직후 staleTime/refetch 규칙대로 곧 재검증되어 옛 값은 갱신된다.
const PERSIST_DOMAINS = new Set(["policy", "crm", "inventory"]);

/** 위 도메인 쿼리 + 성공 상태만 디스크로. 인증·설정 등 다른 쿼리는 남기지 않는다. */
function shouldPersist(query: { queryKey: readonly unknown[]; state: { status: string } }): boolean {
  return PERSIST_DOMAINS.has(query.queryKey[0] as string) && query.state.status === "success";
}

function trimToByteBudget(state: DehydratedState): DehydratedState {
  const encoder = new TextEncoder();
  const recentFirst = [...state.queries].sort(
    (a, b) => (b.state.dataUpdatedAt ?? 0) - (a.state.dataUpdatedAt ?? 0),
  );
  const queries: DehydratedState["queries"] = [];
  let used = encoder.encode(JSON.stringify({ mutations: state.mutations })).byteLength;
  for (const query of recentFirst) {
    let bytes = 0;
    try {
      bytes = encoder.encode(JSON.stringify(query)).byteLength;
    } catch {
      continue;
    }
    if (bytes <= 0 || used + bytes > MAX_PERSIST_BYTES) continue;
    queries.push(query);
    used += bytes;
  }
  return { ...state, queries };
}

/** 부팅(또는 매장 확정) 시 1회: 디스크에 저장된 직전 정책 캐시를 메모리로 끌어온다. */
export async function hydratePolicyCache(
  qc: QueryClient,
  tenantId: string | null,
  shouldHydrate: () => boolean = () => true,
): Promise<void> {
  try {
    const saved = await idbGet<PersistedBlob>(persistKey(tenantId));
    if (!saved) return;
    if (saved.buster !== BUSTER || Date.now() - saved.savedAt > MAX_AGE_MS) return;
    // 계정/매장 전환 중 IndexedDB 읽기가 늦게 끝나도 앞 계정 캐시를 새 스코프에 되살리지 않는다.
    if (!shouldHydrate()) return;
    // dehydrate 가 만든 형태를 그대로 되돌린다. 기존 메모리 쿼리가 더 최신이면 react-query 가 유지.
    hydrate(qc, saved.state as Parameters<typeof hydrate>[1]);
  } catch {
    // 디스크 캐시 장애는 무시 — 캐시는 가속일 뿐, 없으면 네트워크로 폴백.
  }
}

/** 캐시 변경을 디바운스해 디스크에 계속 반영. 반환된 함수로 구독 해제. */
export function startPersistingPolicyCache(qc: QueryClient, tenantId: string | null): () => void {
  const key = persistKey(tenantId);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => {
    try {
      const state = trimToByteBudget(dehydrate(qc, { shouldDehydrateQuery: shouldPersist }));
      void idbSet(key, { buster: BUSTER, savedAt: Date.now(), state } satisfies PersistedBlob).catch(
        () => {},
      );
    } catch {
      // 직렬화/쓰기 실패 무시
    }
  };
  const unsub = qc.getQueryCache().subscribe(() => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, FLUSH_DEBOUNCE_MS);
  });
  return () => {
    if (timer) clearTimeout(timer);
    unsub();
  };
}

/** 로그아웃 시 호출 — 디스크에 남은 모든 매장의 정책 캐시를 비운다. */
export async function clearPersistedPolicyCache(): Promise<void> {
  try {
    await idbClearAll();
  } catch {
    // 무시
  }
}
